import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { PerpInventoryInitialState, Action } from "../../interface";
import { getPerpInventoryData } from "src/api/sp";
import {
  getFormatedDate,
  getFormattedDateFromDifferentFormate
} from "src/utils/getDates";
import { resetStore } from "src/store/actions";
import {
  dotNetDateFormat,
  monthDateFormat
} from "src/config/constants/genericConstants";

export const fetchPerpInventoryData = createAsyncThunk(
  "dashboard/fetchPerpInventoryData",
  ({ department, storeId }: { department: string; storeId: string }) =>
    getPerpInventoryData({ department, storeId })
);

const initialState: PerpInventoryInitialState = {
  perpInventoryData: [],
  goalData: [],
  currentPage: 1,
  recordsPerPage: 5,
  totalPages: 13,
  dateRange: [],
  status: "idle",
  trendStartDate: getFormatedDate(new Date(), monthDateFormat)
};

const perpInventorySlice = createSlice({
  name: "perpInventory",
  initialState,
  reducers: {
    setPage(state: PerpInventoryInitialState, action: Action) {
      const { page } = action.payload;
      state.currentPage = page;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchPerpInventoryData.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchPerpInventoryData.fulfilled, (state, { payload }) => {
        const data = payload.data;
        state.status = "idle";
        const perpInventoryData = data?.list?.map(
          ({ inventory }: { inventory: number }) => inventory
        );
        state.perpInventoryData = perpInventoryData?.length
          ? perpInventoryData
          : [];
        const dateRange = data?.list?.map(
          ({
            date,
            isPhysicalInventoryWeek
          }: {
            date: string;
            isPhysicalInventoryWeek: boolean;
          }) => {
            const dateString = getFormattedDateFromDifferentFormate(
              date,
              dotNetDateFormat,
              monthDateFormat
            );

            return { date: dateString, isPhysicalInventoryWeek };
          }
        );

        state.dateRange = dateRange?.length ? dateRange : [];
        const goalData = data?.list?.map(() => data.goal);
        state.goalData = goalData?.length ? goalData : [];
      })
      .addCase(fetchPerpInventoryData.rejected, (state) => {
        state.status = "error";
        state.perpInventoryData = [];
        state.dateRange = [];
        state.goalData = [];
      });
  }
});

export const { setPage } = perpInventorySlice.actions;

export const selectPerpInventoryData = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.perpInventoryData;

export const selectGoalData = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.goalData;

export const selectCurrentPage = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.currentPage;

export const selectTotalPages = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.totalPages;

export const selectDateRange = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.dateRange;

export const selectPerpInventoryAPIStatus = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.status;

export const selectTrendStartDate = ({
  perpInventory
}: {
  perpInventory: PerpInventoryInitialState;
}) => perpInventory.trendStartDate;

export default perpInventorySlice.reducer;
